import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Avatar,
  Chip,
  Button,
  IconButton,
  Slider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Rating,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Alert
} from '@mui/material';
import {
  Favorite as LikeIcon,
  FavoriteBorder as UnlikeIcon,
  Visibility as ViewIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Wc as GenderIcon,
  Cake as AgeIcon,
  Verified as VerifiedIcon,
  Star as StarIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingIcon
} from '@mui/icons-material';
import api from '../../services/api';
import toast from 'react-hot-toast';
import moment from 'moment';

const UserMatching = () => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [viewMode, setViewMode] = useState('matches');
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    age_min: 18,
    age_max: 60,
    gender: '',
    religion: '',
    community: '',
    country: '',
    state: '',
    city: '',
    education: '',
    profession: '',
    height_min: '',
    height_max: '',
    manglik: '',
    diet: ''
  });
  
  const [masterData, setMasterData] = useState({
    religions: [],
    communities: [],
    countries: [],
    states: [],
    cities: [],
    qualifications: [],
    workingAs: [],
    diets: []
  });

  useEffect(() => {
    fetchMasterData();
    fetchStats();
    fetchUsersList();
  }, []);

  const fetchMasterData = async () => {
    try {
      const response = await api.get('/settings/master-data');
      setMasterData(response.data.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/matching/stats');
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUsersList = async () => {
    try {
      const response = await api.get('/users', { params: { limit: 100 } });
      setUsers(response.data.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMatches = async () => {
    if (!selectedUserId) {
      toast.error('Please select a user');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.get(`/matching/potential-matches/${selectedUserId}`, {
        params: { ...filters, page, limit: 12 }
      });
      setMatches(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      
      // Fetch suggestions for the selected user
      const suggestionsRes = await api.get(`/matching/suggestions/${selectedUserId}`, {
        params: { limit: 6 }
      });
      setSuggestions(suggestionsRes.data.data);
    } catch (error) {
      toast.error('Failed to fetch matches');
    } finally {
      setLoading(false);
    }
  };

  const handleUserChange = (userId) => {
    setSelectedUserId(userId);
    const user = users.find(u => u.id == userId);
    setSelectedUser(user);
  };

  const handleSearch = () => {
    setPage(1);
    fetchMatches();
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleViewProfile = (match) => {
    setSelectedMatch(match);
    setShowProfileDialog(true);
  };

  const getCompatibilityColor = (score) => {
    if (score >= 80) return '#4caf50';
    if (score >= 60) return '#2196f3';
    if (score >= 40) return '#ff9800';
    return '#f44336';
  };

  const getCompatibilityLevel = (score) => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    if (score >= 40) return 'Average Match';
    return 'Low Compatibility';
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        User Matching System
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h6">Total Users</Typography>
              <Typography variant="h3">{stats?.total_users || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h6">Male</Typography>
              <Typography variant="h3">
                {stats?.gender_stats?.find(g => g.gender === 'male')?.count || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h6">Female</Typography>
              <Typography variant="h3">
                {stats?.gender_stats?.find(g => g.gender === 'female')?.count || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: '#fff' }}>
            <CardContent>
              <Typography variant="h6">Avg Age</Typography>
              <Typography variant="h3">28</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Sidebar - User Selection & Filters */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Select User</Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Choose User</InputLabel>
              <Select
                value={selectedUserId}
                label="Choose User"
                onChange={(e) => handleUserChange(e.target.value)}
              >
                {users.map(user => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.phone})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          {selectedUser && (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar src={selectedUser.profile_photo_url} sx={{ width: 60, height: 60 }}>
                  {selectedUser.first_name?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {selectedUser.first_name} {selectedUser.last_name}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    ID: {selectedUser.id} | {selectedUser.phone}
                  </Typography>
                  {selectedUser.is_verified && (
                    <Chip icon={<VerifiedIcon />} label="Verified" size="small" color="success" />
                  )}
                </Box>
              </Box>
            </Paper>
          )}

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Filters</Typography>
            
            <Typography gutterBottom>Age Range</Typography>
            <Slider
              value={[filters.age_min, filters.age_max]}
              onChange={(e, newValue) => {
                setFilters({ ...filters, age_min: newValue[0], age_max: newValue[1] });
              }}
              valueLabelDisplay="auto"
              min={18}
              max={60}
              sx={{ mb: 2 }}
            />
            
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Gender</InputLabel>
              <Select
                value={filters.gender}
                label="Gender"
                onChange={(e) => handleFilterChange('gender', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Religion</InputLabel>
              <Select
                value={filters.religion}
                label="Religion"
                onChange={(e) => handleFilterChange('religion', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {masterData.religions?.map(r => (
                  <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Community</InputLabel>
              <Select
                value={filters.community}
                label="Community"
                onChange={(e) => handleFilterChange('community', e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {masterData.communities?.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={!selectedUserId}
              sx={{
                background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)',
                mt: 2
              }}
            >
              Find Matches
            </Button>
          </Paper>
        </Grid>

        {/* Right Content - Matches */}
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {selectedUser ? `Potential Matches for ${selectedUser.first_name}` : 'Select a user to see matches'}
              </Typography>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchMatches}
                disabled={!selectedUserId}
              >
                Refresh
              </Button>
            </Box>
            
            {loading ? (
              <Box display="flex" justifyContent="center" py={5}>
                <CircularProgress />
              </Box>
            ) : matches.length === 0 ? (
              <Box textAlign="center" py={5}>
                <Typography color="textSecondary">No matches found. Try adjusting filters.</Typography>
              </Box>
            ) : (
              <>
                <Grid container spacing={2}>
                  {matches.map((match) => (
                    <Grid item xs={12} sm={6} md={4} key={match.id}>
                      <Card sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        position: 'relative',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 3
                        }
                      }}>
                        {/* Compatibility Badge */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 1
                          }}
                        >
                          <Chip
                            label={`${match.compatibility_score}% Match`}
                            size="small"
                            sx={{
                              bgcolor: getCompatibilityColor(match.compatibility_score),
                              color: '#fff',
                              fontWeight: 'bold'
                            }}
                          />
                        </Box>
                        
                        {/* Profile Image */}
                        <CardMedia
                          component="img"
                          height="200"
                          image={match.profile_photo_url || '/default-avatar.png'}
                          alt={match.first_name}
                          sx={{ objectFit: 'cover' }}
                        />
                        
                        <CardContent sx={{ flexGrow: 1 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography variant="h6" fontWeight="bold">
                              {match.first_name} {match.last_name}
                            </Typography>
                            {match.is_verified && (
                              <VerifiedIcon sx={{ color: '#4caf50', fontSize: 16 }} />
                            )}
                          </Box>
                          
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <AgeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2">{match.age} years</Typography>
                            <GenderIcon sx={{ fontSize: 14, color: 'text.secondary', ml: 1 }} />
                            <Typography variant="body2">{match.gender}</Typography>
                          </Box>
                          
                          {match.religion && (
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              {match.religion}{match.community ? ` - ${match.community}` : ''}
                            </Typography>
                          )}
                          
                          {match.city && (
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                              <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" color="textSecondary">{match.city}</Typography>
                            </Box>
                          )}
                          
                          {match.working_as && (
                            <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                              <WorkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" color="textSecondary">{match.working_as}</Typography>
                            </Box>
                          )}
                          
                          {match.qualification && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <SchoolIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" color="textSecondary">{match.qualification}</Typography>
                            </Box>
                          )}
                          
                          <Divider sx={{ my: 1.5 }} />
                          
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Chip 
                              label={getCompatibilityLevel(match.compatibility_score)}
                              size="small"
                              sx={{ 
                                bgcolor: getCompatibilityColor(match.compatibility_score) + '20',
                                color: getCompatibilityColor(match.compatibility_score),
                                fontWeight: 'bold'
                              }}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewProfile(match)}
                              sx={{ borderColor: '#e53935', color: '#e53935' }}
                            >
                              View Profile
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => {
                      setPage(value);
                      fetchMatches();
                    }}
                    color="primary"
                  />
                </Box>
              </>
            )}
          </Paper>

          {/* Quick Suggestions */}
          {suggestions.length > 0 && (
            <Paper sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                <TrendingIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Quick Suggestions
              </Typography>
              <Grid container spacing={2}>
                {suggestions.map((suggestion) => (
                  <Grid item xs={12} sm={6} md={4} key={suggestion.id}>
                    <Card sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
                      <Avatar src={suggestion.profile_photo_url} sx={{ width: 50, height: 50, mr: 2 }}>
                        {suggestion.first_name?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {suggestion.first_name} {suggestion.last_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {suggestion.age} yrs • {suggestion.gender}
                        </Typography>
                        {suggestion.city && (
                          <Typography variant="caption" display="block" color="textSecondary">
                            {suggestion.city}
                          </Typography>
                        )}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Profile Dialog */}
      <Dialog open={showProfileDialog} onClose={() => setShowProfileDialog(false)} maxWidth="md" fullWidth>
        {selectedMatch && (
          <>
            <DialogTitle sx={{ 
              background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)', 
              color: '#fff'
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  {selectedMatch.first_name} {selectedMatch.last_name}
                </Typography>
                <IconButton onClick={() => setShowProfileDialog(false)} sx={{ color: '#fff' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <img 
                    src={selectedMatch.profile_photo_url || '/default-avatar.png'} 
                    alt={selectedMatch.first_name}
                    style={{ width: '100%', borderRadius: 8 }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" gutterBottom>
                    {selectedMatch.first_name} {selectedMatch.last_name}
                    {selectedMatch.is_verified && (
                      <VerifiedIcon sx={{ ml: 1, color: '#4caf50', verticalAlign: 'middle' }} />
                    )}
                  </Typography>
                  
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Age</Typography>
                      <Typography>{selectedMatch.age} years</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Gender</Typography>
                      <Typography>{selectedMatch.gender}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Religion</Typography>
                      <Typography>{selectedMatch.religion || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Community</Typography>
                      <Typography>{selectedMatch.community || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Location</Typography>
                      <Typography>{selectedMatch.city || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Profession</Typography>
                      <Typography>{selectedMatch.working_as || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Education</Typography>
                      <Typography>{selectedMatch.qualification || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="textSecondary">Height</Typography>
                      <Typography>{selectedMatch.height_cm ? `${selectedMatch.height_cm} cm` : 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">About</Typography>
                    <Typography variant="body2">{selectedMatch.about_me || 'No description provided.'}</Typography>
                  </Box>
                  
                  <Box mt={2} p={2} sx={{ bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Compatibility Analysis</Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Box textAlign="center">
                        <Typography variant="h3" fontWeight="bold" color={getCompatibilityColor(selectedMatch.compatibility_score)}>
                          {selectedMatch.compatibility_score}%
                        </Typography>
                        <Typography variant="caption">Match Score</Typography>
                      </Box>
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight="bold">
                          {getCompatibilityLevel(selectedMatch.compatibility_score)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Based on age, religion, location, and lifestyle preferences
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setShowProfileDialog(false)}>Close</Button>
              <Button 
                variant="contained" 
                startIcon={<LikeIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #e53935 0%, #1e88e5 100%)'
                }}
              >
                Recommend to User
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default UserMatching;